import { useEffect, useState } from "react";
import VuiInput from "components/VuiInput";
import VuiAutocomplete from "components/AutoComplete";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import useCommon from "hooks/useCommon";
import { clearOperationsSearch, searchOperations } from "store/slice/operationsSlice";
import dayjs from "dayjs";
import HighlightedText from "./HighlightedText";

export default function OperationsSearch({ hotelId, onSelect, input, onInputChange }) {
  const { DISPATCH, STORE } = useCommon();
  const { searchSuggestions, searchLoading, searchError } = STORE((state) => state.operations);
  const [pending, setPending] = useState(false);
  const [open, setOpen] = useState(false);
  const query = input.trim();

  useEffect(() => {
    DISPATCH(clearOperationsSearch());
    if (!hotelId || query.length < 3) {
      setPending(false);
      return;
    }
    setPending(true);
    let request;
    const timer = setTimeout(() => {
      request = DISPATCH(searchOperations({ hotelId, q: query, limit: 20 }));
      setPending(false);
    }, 350);
    return () => {
      clearTimeout(timer);
      request?.abort();
      DISPATCH(clearOperationsSearch());
    };
  }, [DISPATCH, hotelId, query]);

  const errorMessage = searchError
    ? typeof searchError === "string"
      ? searchError
      : searchError.message || "Search failed. Try again."
    : "";

  return (
    <VuiAutocomplete
      fullWidth
      minWidth={0}
      slotProps={{
        popper: {
          sx: (theme) => ({
            zIndex: 1300,
            "& .MuiPaper-root": {
              background: theme.palette.light.main,
              color: theme.palette.dark.main,
              border: `1px solid ${theme.palette.primary.main}40`,
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(27,46,36,0.16)",
            },
            "& .MuiAutocomplete-option": {
              color: theme.palette.dark.main,
              "&:hover, &.Mui-focused, &[aria-selected='true']": {
                backgroundColor: "rgba(27,46,36,0.08)",
              },
            },
            "& .MuiAutocomplete-loading, & .MuiAutocomplete-noOptions": {
              color: theme.palette.dark.main,
              fontSize: "12px",
            },
          }),
        },
      }}
      disabled={!hotelId}
      clearOnBlur={false}
      disableClearable={false}
      value={null}
      inputValue={input}
      open={open && query.length >= 3 && !errorMessage}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={query.length >= 3 ? searchSuggestions : []}
      filterOptions={(options) => options}
      loading={pending || searchLoading}
      loadingText="Searching..."
      noOptionsText="No results found"
      getOptionLabel={(option) => `${option.templateName} — ${option.operationDateKey}`}
      isOptionEqualToValue={(option, value) =>
        option.templateId === value.templateId && option.operationDateKey === value.operationDateKey
      }
      onInputChange={(_, value, reason) => {
        if (reason === "reset") return;
        setOpen(reason === "input");
        if (value.slice(0, 500).trim() !== query) {
          DISPATCH(clearOperationsSearch());
          setPending(value.trim().length >= 3);
        }
        onInputChange(value.slice(0, 500));
      }}
      onChange={(_, suggestion) => {
        if (!suggestion) return;
        onSelect(suggestion);
        setOpen(false);
      }}
      renderOption={(props, option) => (
        <li {...props} key={`${option.templateId}-${option.operationDateKey}`}>
          <VuiBox sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
            <VuiTypography variant="button" color="inherit" display="block" noWrap title={option.templateName}>
              <HighlightedText text={option.templateName} query={query} />
            </VuiTypography>
            <VuiTypography variant="caption" color="inherit" display="block">
              {dayjs(option.operationDateKey).format("MMM D, YYYY")}
            </VuiTypography>
          </VuiBox>
        </li>
      )}
      renderInput={(params) => (
        <VuiBox>
          <VuiInput
            {...params.InputProps}
            id={params.id}
            disabled={params.disabled}
            fullWidth
            gradient
            placeholder="Search..."
            error={Boolean(errorMessage)}
            inputProps={{ ...params.inputProps, maxLength: 500, "aria-label": "Search" }}
          />
          {errorMessage && (
            <VuiTypography variant="caption" color="error" role="alert">
              {errorMessage}
            </VuiTypography>
          )}
        </VuiBox>
      )}
    />
  );
}
